import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Section, SectionHeading, Reveal } from './ui/Section';

/**
 * 回答は既存の製品コピーで裏付けが取れている内容のみを記載している。
 * 他社データの移行可否・対応OS・サポート時間などは事実確認が取れ次第追加すること。
 */
const QA = [
  {
    q: '会計データはどこに保存されますか？',
    a: 'お客様のPC内にのみ保存されます。外部接続は一切不要で、データが社外のサーバーを経由することはありません。クラウドへの依存がゼロのため、情報漏洩のリスクを構造的に断つことができます。',
  },
  {
    q: 'AIの利用に回数制限はありますか？',
    a: 'ありません。質問回数は無制限、AI-OCRも無制限でご利用いただけます。AIがお客様のPC内で動作するため、外部サービスの従量課金が発生せず、回数を気にせずお使いいただけます。',
  },
  {
    q: '何名まで利用できますか？',
    a: 'スタンダードプランで5名まで対応しています。仕訳入力の件数に上限はありません。',
  },
  {
    q: '料金以外に費用はかかりますか？',
    a: '初期費用は0円です。一括支払で年5,900円（月あたり492円）のみで、全機能をご利用いただけます。月払いをご希望の場合は980円/月です。',
  },
  {
    q: '決算書や消費税申告書も作成できますか？',
    a: '財務諸表の自動作成、消費税申告書の自動生成、決算整理仕訳の自動化に対応しています。日々の記帳がそのまま決算につながる設計です。',
  },
  {
    q: '会計の専門知識がなくても使えますか？',
    a: '日本語で取引内容を入力するだけで、AIが勘定科目と税区分を判定し仕訳に変換します。専門用語や複雑な操作手順を覚える必要はありません。',
  },
  {
    q: '無料体験を始めるのにクレジットカードは必要ですか？',
    a: '必要ありません。クレジットカード不要で、すぐに始めていただけます。',
  },
  {
    q: '無料体験の期間が終わったらどうなりますか？',
    a: '自動的に課金されることはありません。クレジットカードのご登録も不要です。継続してご利用いただく場合のみ、担当者よりお手続きをご案内いたします。ご不要の場合はそのままで結構です。',
  },
  {
    q: '導入時のサポートはありますか？',
    a: 'お申し込み後2営業日以内に担当者よりご連絡し、インストールから初期設定までご案内いたします。ご利用開始後のご質問はメールサポートで承ります。',
  },
];

const Item: React.FC<{ q: string; a: string; index: number }> = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  const id = `faq-panel-${index}`;

  return (
    <div className="border-b border-line">
      <h3>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={id}
          className="flex w-full items-start justify-between gap-6 py-6 text-left"
        >
          <span className="flex gap-4">
            <span className="num shrink-0 text-lg font-bold text-brand-500">Q</span>
            <span className="font-bold leading-relaxed text-ink-900">{q}</span>
          </span>
          <span
            className={`mt-1 shrink-0 transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-body-faint" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="flex gap-4 pb-7 pr-10">
              <span className="num shrink-0 text-lg font-bold text-ink-300">A</span>
              <p className="text-small leading-relaxed text-body-soft">{a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Faq: React.FC = () => (
  <Section id="faq" tone="white">
    <SectionHeading eyebrow="FAQ" title="よくあるご質問" />

    <Reveal delay={0.1}>
      <div className="mx-auto mt-12 max-w-3xl rounded-card border border-line bg-white px-6 sm:px-10">
        {QA.map((item, i) => (
          <Item key={item.q} q={item.q} a={item.a} index={i} />
        ))}
      </div>
    </Reveal>

    <Reveal delay={0.2}>
      <p className="mt-8 text-center text-small text-body-soft">
        その他のご質問は{' '}
        <a href="/monitor.html" className="font-bold text-brand-600 underline underline-offset-4 hover:text-brand-700">
          お問い合わせフォーム
        </a>{' '}
        よりお気軽にどうぞ。
      </p>
    </Reveal>
  </Section>
);

export default Faq;
